import React, { useMemo } from 'react';
import { Card } from '../components/ui';
import { MiniArea, useChartWidth } from '../components/charts';
import { developmentLevelLabel, isDevelopmentAssessment } from '../../shared/developmentLevels';

export default function CourseAssessmentReport({ items, selectedId, setSelectedId, formatDate }) {
  const width = useChartWidth(90);
  const safeItems = Array.isArray(items) ? items : [];

  const trend = useMemo(() => {
    const rows = (items || [])
      .filter(e => !isDevelopmentAssessment(e) && e.result_status !== 'absent')
      .map((e) => ({ date: e?.date, value: e?.score != null ? Number(e.score) : null }))
      .filter((r) => r.date && r.value != null && !Number.isNaN(r.value))
      .sort((a, b) => {
        const ta = new Date(a.date).getTime();
        const tb = new Date(b.date).getTime();
        if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
        if (Number.isNaN(ta)) return 1;
        if (Number.isNaN(tb)) return -1;
        return ta - tb;
      });
    const values = rows.map((r) => r.value);
    const dates = rows.map((r) => r.date);
    const avg = values.length ? (values.reduce((acc, v) => acc + v, 0) / values.length) : null;
    return { values, dates, avg };
  }, [items]);

  if (!safeItems.length) return null;

  const selected = (safeItems && safeItems.length)
    ? (safeItems.find(e => String(e.id) === String(selectedId)) || safeItems[0])
    : null;

  const StatPill = ({ label, value, strong }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
      <span style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', fontSize: 13, color: strong ? '#0F1E56' : '#334155', background: '#f8fafc', fontWeight: strong ? 700 : 500 }}>
        {value ?? '-'}
      </span>
    </div>
  );

  return (
    <Card title="Genel Değerlendirme">
      <div style={{ marginBottom: 10 }}>
        <label htmlFor="general-eval-select" style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Sınav seç</label>
        <select
          id="general-eval-select"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          style={{ width: '100%', minHeight: 44, padding: 10, borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}
        >
          {safeItems.map(ex => (
            <option key={ex.id} value={ex.id}>
              {formatDate(ex.date)}{ex.lesson_name ? ` • ${ex.lesson_name}` : ''}{ex.title ? ` • ${ex.title}` : ''}{developmentLevelLabel(ex.development_level) ? ` • ${developmentLevelLabel(ex.development_level)}` : ex.score != null ? ` • ${ex.score}` : ''}
            </option>
          ))}
        </select>
      </div>

      {!!trend.values.length && (
        <div style={{ background: '#fff', border: '1px solid #eef2f7', borderRadius: 12, padding: 8, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Puan Grafiği</div>
          <MiniArea values={trend.values} dates={trend.dates} showAvg={true} avg={trend.avg} width={width} />
        </div>
      )}

      {selected && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: 10 }}>
            <StatPill label="Sınav Tarihi" value={formatDate(selected.date)} />
            <StatPill label="Başlık" value={selected.title || '-'} />
            {selected.lesson_name && <StatPill label="Ders" value={selected.lesson_name} />}
            <StatPill label={isDevelopmentAssessment(selected) ? 'Gelişim Düzeyi' : 'Puan'}
              value={selected.result_status === 'absent' ? 'Katılmadı' : isDevelopmentAssessment(selected)
                ? developmentLevelLabel(selected.development_level) || 'Sonuç bekleniyor'
                : selected.score != null ? String(selected.score) : 'Sonuç bekleniyor'} strong />
          </div>
          {(selected.class_name || selected.notes) && (
            <div style={{ display: 'grid', gap: 6 }}>
              {selected.class_name && (
                <div style={{ fontSize: 13, color: '#334155' }}>
                  <span style={{ color: '#64748b' }}>Sınıf:</span> {selected.class_name}
                </div>
              )}
              {selected.notes && (
                <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap' }}>
                  <span style={{ color: '#64748b' }}>Not:</span> {selected.notes}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
